import { z } from 'zod';
import { AuditedFields, CursorPageQuery, IsoDate, IsoDateTime, QueryBoolean, Uuid, Version, } from './common.js';
/**
 * Horevia Inventory (ADR-020, module `inventory`) : lieux de stockage et
 * articles par établissement, niveaux matérialisés, journal de mouvements
 * immuable, recettes de déstockage, achats et inventaires physiques.
 *
 * Les quantités sont des nombres décimaux (3 décimales au plus) exprimés dans
 * l'**unité de gestion** de l'article ; les achats se saisissent en
 * conditionnements (`packageQuantity` de l'article).
 */
export const StockItemCategory = z.enum(['BEVERAGE', 'FOOD', 'SUPPLY', 'OTHER']);
export const StockUnit = z.enum(['PIECE', 'BOTTLE', 'CAN', 'L', 'CL', 'KG', 'G', 'PORTION']);
export const StockMovementType = z.enum([
    'PURCHASE',
    'SALE',
    'SALE_REVERSAL',
    'ADJUSTMENT',
    'LOSS',
    'TRANSFER_OUT',
    'TRANSFER_IN',
    'COUNT',
]);
/** Quantité en unité de gestion : au plus trois décimales. */
export const StockQuantity = z.number().finite().multipleOf(0.001);
const StockCode = z
    .string()
    .trim()
    .min(2)
    .max(20)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'Code : lettres, chiffres, point, tiret');
// ---------------------------------------------------------------------------
// Lieux de stockage
// ---------------------------------------------------------------------------
export const StockLocation = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    /** Point de vente servi depuis ce lieu (déstockage à la vente, R-INV-02). */
    outletId: Uuid.nullable(),
    code: z.string(),
    name: z.string(),
    /** Lieu par défaut de l'établissement (un seul) : réception des achats, ventes sans lieu de point de vente. */
    isDefault: z.boolean(),
    isActive: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateStockLocationRequest = z.object({
    propertyId: Uuid,
    code: StockCode,
    name: z.string().trim().min(2).max(80),
    outletId: Uuid.nullable().optional(),
    isDefault: z.boolean().optional(),
});
export const UpdateStockLocationRequest = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    outletId: Uuid.nullable().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    version: Version,
});
export const ListStockLocationsQuery = z.object({
    propertyId: Uuid.optional(),
    includeInactive: QueryBoolean.optional(),
});
// ---------------------------------------------------------------------------
// Articles
// ---------------------------------------------------------------------------
export const StockItem = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    code: z.string(),
    name: z.string(),
    category: StockItemCategory,
    unit: StockUnit,
    /** Conditionnement d'achat (« casier de 24 », « bouteille 70 cl »). */
    packageLabel: z.string().nullable(),
    /** Unités de gestion par conditionnement (> 0 ; 1 = achat à l'unité). */
    packageQuantity: z.number(),
    /** Seuil d'alerte en unité de gestion ; `null` = pas d'alerte de seuil. */
    minQuantity: z.number().nullable(),
    /** Coût moyen pondéré par unité de gestion (R-INV-05). */
    averageUnitCost: z.number(),
    lastUnitCost: z.number().nullable(),
    currencyCode: z.string(),
    isActive: z.boolean(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateStockItemRequest = z.object({
    propertyId: Uuid,
    code: StockCode,
    name: z.string().trim().min(2).max(120),
    category: StockItemCategory,
    unit: StockUnit,
    packageLabel: z.string().trim().max(60).nullable().optional(),
    packageQuantity: z.number().positive().multipleOf(0.001).max(100_000).optional(),
    minQuantity: StockQuantity.nonnegative().nullable().optional(),
});
export const UpdateStockItemRequest = z.object({
    name: z.string().trim().min(2).max(120).optional(),
    category: StockItemCategory.optional(),
    unit: StockUnit.optional(),
    packageLabel: z.string().trim().max(60).nullable().optional(),
    packageQuantity: z.number().positive().multipleOf(0.001).max(100_000).optional(),
    minQuantity: StockQuantity.nonnegative().nullable().optional(),
    isActive: z.boolean().optional(),
    version: Version,
});
export const ListStockItemsQuery = z.object({
    propertyId: Uuid.optional(),
    category: StockItemCategory.optional(),
    q: z.string().trim().max(80).optional(),
    includeInactive: QueryBoolean.optional(),
});
// ---------------------------------------------------------------------------
// Niveaux et alertes
// ---------------------------------------------------------------------------
export const StockLevel = z.object({
    locationId: Uuid,
    locationName: z.string(),
    quantity: z.number(),
    updatedAt: IsoDateTime.nullable(),
});
/** Niveaux d'un article : par lieu, total, alerte (R-INV-04) et valeur au coût moyen. */
export const StockItemLevels = z.object({
    item: StockItem,
    levels: z.array(StockLevel),
    total: z.number(),
    isAlert: z.boolean(),
    /** `total × coût moyen`, arrondi à la devise. */
    value: z.number(),
});
export const StockLevelsQuery = z.object({
    propertyId: Uuid,
    locationId: Uuid.optional(),
    category: StockItemCategory.optional(),
    alertsOnly: QueryBoolean.optional(),
    q: z.string().trim().max(80).optional(),
});
export const StockSummary = z.object({
    propertyId: Uuid,
    currencyCode: z.string(),
    itemsCount: z.number().int(),
    alertsCount: z.number().int(),
    /** Valeur totale du stock au coût moyen. */
    value: z.number(),
    locationsCount: z.number().int(),
});
// ---------------------------------------------------------------------------
// Mouvements
// ---------------------------------------------------------------------------
export const StockMovement = z.object({
    id: Uuid,
    propertyId: Uuid,
    itemId: Uuid,
    itemCode: z.string(),
    itemName: z.string(),
    unit: StockUnit,
    locationId: Uuid,
    locationName: z.string(),
    type: StockMovementType,
    /** Signée : positive en entrée, négative en sortie. */
    quantity: z.number(),
    /** Niveau du lieu après le mouvement. */
    quantityAfter: z.number(),
    unitCost: z.number().nullable(),
    reason: z.string().nullable(),
    sourceType: z.string().nullable(),
    sourceId: Uuid.nullable(),
    /** Référence lisible de la source (commande, bon d'achat, inventaire). */
    sourceReference: z.string().nullable(),
    transferId: Uuid.nullable(),
    occurredAt: IsoDateTime,
    createdAt: IsoDateTime,
    createdByName: z.string().nullable(),
});
export const ListStockMovementsQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    itemId: Uuid.optional(),
    locationId: Uuid.optional(),
    type: StockMovementType.optional(),
    from: IsoDate.optional(),
    to: IsoDate.optional(),
});
/** Ajustement (quantité signée, non nulle) ou perte (quantité positive retirée). */
export const CreateStockMovementRequest = z
    .object({
    itemId: Uuid,
    locationId: Uuid,
    type: z.enum(['ADJUSTMENT', 'LOSS']),
    quantity: StockQuantity,
    reason: z.string().trim().min(3).max(200),
    /** Coût unitaire d'un ajustement positif (sinon coût moyen de l'article). */
    unitCost: z.number().nonnegative().optional(),
    occurredAt: IsoDateTime.optional(),
})
    .superRefine((input, ctx) => {
    if (input.quantity === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quantity'],
            message: 'La quantité ne peut pas être nulle',
        });
    }
    if (input.type === 'LOSS' && input.quantity < 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quantity'],
            message: 'Une perte se saisit en quantité positive',
        });
    }
});
export const CreateStockTransferRequest = z
    .object({
    itemId: Uuid,
    fromLocationId: Uuid,
    toLocationId: Uuid,
    quantity: StockQuantity.positive(),
    reason: z.string().trim().min(3).max(200),
    occurredAt: IsoDateTime.optional(),
})
    .superRefine((input, ctx) => {
    if (input.fromLocationId === input.toLocationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['toLocationId'],
            message: 'Les deux lieux doivent être différents',
        });
    }
});
export const StockTransferResult = z.object({ out: StockMovement, in: StockMovement });
// ---------------------------------------------------------------------------
// Recettes de déstockage (H5.2)
// ---------------------------------------------------------------------------
export const ProductRecipeLine = z.object({
    itemId: Uuid,
    itemCode: z.string(),
    itemName: z.string(),
    unit: StockUnit,
    /** Quantité consommée par unité vendue, en unité de gestion de l'article. */
    quantity: z.number(),
});
export const ProductRecipe = z.object({
    productId: Uuid,
    lines: z.array(ProductRecipeLine),
});
export const PutProductRecipeRequest = z.object({
    lines: z
        .array(z.object({ itemId: Uuid, quantity: StockQuantity.positive() }))
        .max(30)
        .superRefine((lines, ctx) => {
        const seen = new Set();
        lines.forEach((line, index) => {
            if (seen.has(line.itemId)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [index, 'itemId'],
                    message: 'Article en double dans la recette',
                });
            }
            seen.add(line.itemId);
        });
    }),
});
// ---------------------------------------------------------------------------
// Achats (H5.3)
// ---------------------------------------------------------------------------
export const StockPurchaseStatus = z.enum(['DRAFT', 'RECEIVED', 'CANCELLED']);
export const StockPurchaseLine = z.object({
    id: Uuid,
    itemId: Uuid,
    itemCode: z.string(),
    itemName: z.string(),
    unit: StockUnit,
    packageLabel: z.string().nullable(),
    /** Nombre de conditionnements achetés. */
    packageQuantity: z.number(),
    /** Unités de gestion correspondantes (conditionnements × `packageQuantity` de l'article). */
    unitQuantity: z.number(),
    /** Coût par unité de gestion. */
    unitCost: z.number(),
    lineCost: z.number(),
});
export const StockPurchase = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    locationId: Uuid,
    locationName: z.string(),
    reference: z.string(),
    supplierName: z.string(),
    supplierReference: z.string().nullable(),
    status: StockPurchaseStatus,
    orderedAt: IsoDate,
    receivedAt: IsoDateTime.nullable(),
    notes: z.string().nullable(),
    totalCost: z.number(),
    currencyCode: z.string(),
    lines: z.array(StockPurchaseLine),
    version: Version,
})
    .merge(AuditedFields);
const PurchaseLineInput = z.object({
    itemId: Uuid,
    packageQuantity: z.number().positive().multipleOf(0.001).max(100_000),
    /** Coût d'un conditionnement (le coût unitaire en est déduit). */
    packageCost: z.number().nonnegative(),
});
export const CreateStockPurchaseRequest = z.object({
    propertyId: Uuid,
    locationId: Uuid,
    supplierName: z.string().trim().min(2).max(120),
    supplierReference: z.string().trim().max(60).nullable().optional(),
    orderedAt: IsoDate.optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    lines: z.array(PurchaseLineInput).min(1).max(100),
});
export const UpdateStockPurchaseRequest = z.object({
    locationId: Uuid.optional(),
    supplierName: z.string().trim().min(2).max(120).optional(),
    supplierReference: z.string().trim().max(60).nullable().optional(),
    orderedAt: IsoDate.optional(),
    notes: z.string().trim().max(500).nullable().optional(),
    lines: z.array(PurchaseLineInput).min(1).max(100).optional(),
    version: Version,
});
export const ReceiveStockPurchaseRequest = z.object({
    version: Version,
    receivedAt: IsoDateTime.optional(),
});
export const CancelStockPurchaseRequest = z.object({
    version: Version,
    reason: z.string().trim().min(3).max(200),
});
export const ListStockPurchasesQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    status: StockPurchaseStatus.optional(),
    q: z.string().trim().max(80).optional(),
});
// ---------------------------------------------------------------------------
// Inventaires physiques (H5.4)
// ---------------------------------------------------------------------------
export const StockCountStatus = z.enum(['OPEN', 'VALIDATED', 'CANCELLED']);
export const StockCountLine = z.object({
    id: Uuid,
    itemId: Uuid,
    itemCode: z.string(),
    itemName: z.string(),
    unit: StockUnit,
    /** Quantité théorique figée à l'ouverture (R-INV-06). */
    expectedQuantity: z.number(),
    countedQuantity: z.number().nullable(),
    /** `counted − expected` ; `null` tant que la ligne n'est pas comptée. */
    variance: z.number().nullable(),
});
export const StockCount = z
    .object({
    id: Uuid,
    propertyId: Uuid,
    locationId: Uuid,
    locationName: z.string(),
    reference: z.string(),
    status: StockCountStatus,
    startedAt: IsoDateTime,
    validatedAt: IsoDateTime.nullable(),
    validatedByName: z.string().nullable(),
    notes: z.string().nullable(),
    lines: z.array(StockCountLine),
    /** Lignes comptées / lignes totales. */
    countedLines: z.number().int(),
    version: Version,
})
    .merge(AuditedFields);
export const CreateStockCountRequest = z.object({
    locationId: Uuid,
    notes: z.string().trim().max(500).nullable().optional(),
    /** Restreindre le comptage à une catégorie (sinon tous les articles actifs de l'établissement). */
    category: StockItemCategory.optional(),
});
export const UpdateStockCountLinesRequest = z.object({
    version: Version,
    lines: z
        .array(z.object({ itemId: Uuid, countedQuantity: StockQuantity.nonnegative().nullable() }))
        .min(1)
        .max(500),
});
export const ValidateStockCountRequest = z.object({ version: Version });
export const CancelStockCountRequest = z.object({
    version: Version,
    reason: z.string().trim().min(3).max(200),
});
export const ListStockCountsQuery = CursorPageQuery.extend({
    propertyId: Uuid.optional(),
    locationId: Uuid.optional(),
    status: StockCountStatus.optional(),
});
//# sourceMappingURL=stock.js.map